import React from 'react';
import { CircularProgress } from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CustomButton from '../../../../../components/buttons/CustomButton';
import ProviderRow from './ProviderRow';
import styles from './ProviderListCard.module.css';

const countLabel = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

/** Linhas de carregamento no formato das linhas de operadora. */
function ProviderListSkeleton({ rows = 3 }) {
    return (
        <ul className={styles.list} aria-busy="true" aria-label="Carregando operadoras">
            {Array.from({ length: rows }).map((_, index) => (
                <li key={index} className={styles.skeletonRow}>
                    <span className={`${styles.shimmer} ${styles.skeletonIcon}`} />
                    <span className={styles.skeletonText}>
                        <span className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: `${50 - index * 8}%` }} />
                        <span className={`${styles.shimmer} ${styles.skeletonLineSm}`} />
                    </span>
                    <span className={`${styles.shimmer} ${styles.skeletonTag}`} />
                </li>
            ))}
        </ul>
    );
}

/** Estado vazio: para que serve o cadastro e, para quem pode editar, o atalho da primeira. */
function EmptyProviders({ canEdit, onAdd }) {
    return (
        <div className={styles.empty}>
            <span className={styles.emptyArt} aria-hidden="true">
                <span className={styles.emptyArtBack} />
                <span className={styles.emptyArtIcon}><BadgeOutlinedIcon /></span>
            </span>
            <span className={styles.emptyTitle}>Nenhuma operadora cadastrada</span>
            <span className={styles.emptyText}>
                Cadastre as operadoras que você atende para marcar atendimentos por convênio.
                Depois informe a carteirinha no cadastro de cada pessoa e escolha Convênio ao agendar.
            </span>
            {canEdit && (
                <CustomButton
                    style="main"
                    variant="contained"
                    icon={<AddRoundedIcon />}
                    label="Cadastrar primeira operadora"
                    onClick={onAdd}
                />
            )}
        </div>
    );
}

/** Lista das operadoras: título com o total, contagem de ativas e inativas, carregamento, estado vazio e uma linha por operadora. */
function ProviderListCard({ loading, providers, canEdit, busy, onAdd, onEdit, onToggle, onProcedures }) {
    const activeCount = providers.filter((provider) => Number(provider.active) === 1).length;
    const inactiveCount = providers.length - activeCount;

    return (
        <section className={styles.card}>
            <header className={styles.header}>
                <div className={styles.headerText}>
                    <h3 className={styles.title}>
                        Operadoras cadastradas{!loading && providers.length > 0 ? ` · ${providers.length}` : ''}
                    </h3>
                    {providers.length > 0 && (
                        <span className={styles.subtitle}>
                            {countLabel(activeCount, 'ativa', 'ativas')}
                            {inactiveCount > 0 ? ` · ${countLabel(inactiveCount, 'inativa', 'inativas')}` : ''}
                        </span>
                    )}
                </div>
                {busy && (
                    <span className={styles.busy}>
                        <CircularProgress size={14} />
                        Processando...
                    </span>
                )}
            </header>

            {loading ? (
                <ProviderListSkeleton />
            ) : providers.length === 0 ? (
                <EmptyProviders canEdit={canEdit} onAdd={onAdd} />
            ) : (
                <ul className={styles.list}>
                    {providers.map((provider) => (
                        <ProviderRow
                            key={provider.id}
                            provider={provider}
                            canEdit={canEdit}
                            busy={busy}
                            onEdit={onEdit}
                            onToggle={onToggle}
                            onProcedures={onProcedures}
                        />
                    ))}
                </ul>
            )}
        </section>
    );
}

export default ProviderListCard;
